/**
 * VOLLEYBALL PARTNER DRILLS DATA STRUCTURE - CONSOLIDATED
 *
 * This file contains consolidated volleyball partner drills organized into skill levels:
 * 1. Beginner Partner Passing (30 minutes) - Basic partner passing
 * 2. Intermediate Partner Passing (45 minutes) - Advanced partner passing
 * 3. Advanced Partner Passing (45 minutes) - Complex partner sequences
 * 4. Beginner Partner Setting (30 minutes) - Basic partner setting
 * 5. Intermediate Partner Setting (45 minutes) - Advanced partner setting
 * 6. Advanced Partner Setting (45 minutes) - Complex partner setting
 * 7. Beginner Partner Serving (30 minutes) - Partner serving practice
 * 8. Intermediate Partner Serving (45 minutes) - Advanced partner serving
 * 9. Advanced Partner Serving (45 minutes) - Complex partner serving
 * 10. Complete Partner Volleyball (60 minutes) - All partner skills combined
 *
 * DRILL STRUCTURE:
 * - id: Numeric ID starting from 9501 (partner volleyball drills)
 * - name: Clean drill name (no emojis - for TTS audio generation)
 * - categories: Array of skill categories
 * - sports: Array containing 'volleyball'
 * - difficulty: 'beginner', 'intermediate', or 'advanced'
 * - skill: High-level skill category (e.g., 'Passing', 'Setting', 'Serving')
 * - points: Point value for completion (30-60 points based on time)
 * - instructions: Detailed explanation including drill name introduction
 * - timedInstructions: Array of {time, text} objects for SSML generation
 * - timeTotal: Total duration in seconds (1800-3600 seconds = 30-60 minutes)
 * - equipment: Required equipment (e.g., 'Volleyball, partner')
 *
 * AUDIO GENERATION NOTES:
 * - Instructions field contains drill name introduction
 * - timedInstructions start directly with action steps
 * - SSML breaks are calculated based on time gaps between instructions
 * - Voice used: en-US-Studio-M (male Studio voice that supports SSML)
 *
 * VOLLEYBALL PARTNER DRILL REQUIREMENTS:
 * - All drills require a partner (friend, sibling, parent)
 * - Focus on partner skills and teamwork
 * - Equipment limited to volleyball and some space
 * - Appropriate for 6-10 year olds
 * - Focus on fundamental volleyball partner techniques and coordination
 * - Progressive skill development within each drill
 * - Emphasis on proper form, technique, and teamwork
 *
 * CREATED: [Date] - Volleyball partner drills with SSML audio generation
 * UPDATED: [Date] - Consolidated into skill-based structure with longer drills
 */

const volleyballPartnerDrills = [
  // BEGINNER PARTNER PASSING (30 minutes) Done
  {
    id: 2080,
    name: 'Beginner Partner Passing',
    categories: ['Passing', 'beginner', 'partner'],
    sports: ['volleyball'],
    difficulty: 'beginner',
    skill: 'Passing',
    points: 30,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, check the ceiling, and move breakables. Stand six to eight feet apart or kneel if space is tight. Chest high arcs only. Keep two tracks, knees over toes, tall spine, thumbs together, wrists flat, shoulders forward, light weight split on the balls of the feet, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–216 (movement starts right away)
      {
        time: 0,
        text: 'Ready stance on two tracks. Knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Platform shape. Thumbs together, wrists flat, straight arms, shoulders forward.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      {
        time: 48,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 60,
        text: 'Platform taps. Tap the ball gently up. Arms stay straight.',
      },
      {
        time: 72,
        text: 'Hip hinge. Hips back a little, chest tall, knees over toes. Hold for 3, 2, 1.',
      },
      {
        time: 84,
        text: 'Angle rehearsal. Point platform left, right, then middle. Small moves.',
      },
      {
        time: 96,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      { time: 108, text: 'Reminder. Two tracks, knees over toes, tall spine.' },
      {
        time: 120,
        text: 'Five soft self passes. Light touch to the window, then catch.',
      },
      { time: 132, text: 'Reset posture. Head over hips. Feet quiet.' },
      { time: 144, text: 'Aim chest height. Eyes on the ball.' },
      { time: 156, text: 'Thumbs together on the platform. Wrists stay flat.' },
      {
        time: 168,
        text: 'Shoulders forward. Light weight split on the balls of the feet.',
      },
      { time: 192, text: 'Last warm up cue. Smooth lift from the legs.' },
      { time: 216, text: 'Get ready for partner tosses.' },

      // Stance 1: Stationary partner pass 240–438
      {
        time: 240,
        text: 'Stance 1. Stationary Partner Pass. Platform set to the middle. Hold for 3, 2, 1.',
      },
      {
        time: 258,
        text: 'Partner underhand toss to belly button height. Pass to the chest.',
      },
      {
        time: 276,
        text: 'Lift from your legs. Arms stay straight. Breathe out on the pass.',
      },
      { time: 294, text: 'Aim small. Pick a sticker spot on the shirt.' },
      { time: 312, text: 'Feet quiet. Toes and knees line up on two tracks.' },
      { time: 330, text: 'Switch every five passes. Count together.' },
      {
        time: 348,
        text: 'Pulse. Three tiny knee bends, then one smooth pass.',
      },
      {
        time: 366,
        text: 'Reset shape. Thumbs together, wrists flat. Hold for 3, 2, 1.',
      },
      { time: 384, text: 'Count clean eight in a row as a team.' },
      { time: 402, text: 'Breathe steady. Tall spine.' },
      { time: 420, text: 'Last two passes here. Soft touch.' },
      { time: 438, text: 'Transition. Quick shake out.' },

      // Stance 2: Kneeling platform pass 540–840
      {
        time: 540,
        text: 'Stance 2. Kneeling Platform Pass. Both knees down, tall spine. Hold for 3, 2, 1.',
      },
      {
        time: 558,
        text: 'Partner soft toss to belly button height. Pass back with a gentle lift.',
      },
      { time: 576, text: 'Platform still. Shoulders guide. Arms straight.' },
      { time: 594, text: 'Breathe out on the pass. Quiet knees.' },
      { time: 612, text: 'Switch every five passes. Count together.' },
      {
        time: 630,
        text: 'Angle check. Point your platform to the chest target.',
      },
      {
        time: 648,
        text: 'Pulse. Three pretend passes with no ball. Feel the path.',
      },
      { time: 666, text: 'Count clean eight kneeling passes together.' },
      {
        time: 684,
        text: 'Reset shape. Thumbs together, wrists flat. Hold for 3, 2, 1.',
      },
      { time: 702, text: 'Last round kneeling. Smooth toss, smooth pass.' },
      { time: 720, text: 'Transition. One foot up. Stand slowly. Quiet feet.' },

      // Stance 3: One knee angle pass 840–1140
      {
        time: 840,
        text: 'Stance 3. One Knee Angle Pass. Passing side knee down, other foot forward on two tracks. Hold for 3, 2, 1.',
      },
      {
        time: 858,
        text: 'Partner toss slightly left. Angle platform left, pass to the chest.',
      },
      {
        time: 876,
        text: 'Partner toss slightly right. Angle platform right, pass to the chest.',
      },
      { time: 894, text: 'Front toe points to partner. Small step if needed.' },
      { time: 912, text: 'Switch every five passes. Count together.' },
      {
        time: 930,
        text: 'Cues. Thumbs together, wrists flat, lift from legs.',
      },
      { time: 948, text: 'Count clean six left and six right as a team.' },
      {
        time: 966,
        text: 'Pulse. Two quick angle holds each side. Hold for 3, 2, 1.',
      },
      { time: 984, text: 'Breathe steady. Head over hips.' },
      { time: 1002, text: 'Last round on one knee. Smooth angle, soft pass.' },
      { time: 1020, text: 'Transition. Stand up. Shake out arms.' },

      // Stance 4: Micro shuffle pass left and right 1140–1440
      {
        time: 1140,
        text: 'Stance 4. Micro Shuffle Pass. One small shuffle to line up, then pass to the chest. Hold for 3, 2, 1.',
      },
      {
        time: 1158,
        text: 'Shuffle left one step, stop, platform set, pass. Partner two hand catch.',
      },
      { time: 1176, text: 'Shuffle right one step, stop, platform set, pass.' },
      { time: 1194, text: 'Switch every four passes. Keep the rhythm.' },
      {
        time: 1212,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      {
        time: 1230,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 1248, text: 'Count clean six each side with smooth stops.' },
      { time: 1266, text: 'Aim small. Hit the sticker spot.' },
      { time: 1284, text: 'Breathe out on the pass. Tall spine.' },
      { time: 1302, text: 'Last round here. Small steps, strong platform.' },

      // Stance 5: Receive, set feet, pass, reset 1440–1620 (denser real-time cues)
      {
        time: 1440,
        text: 'Stance 5. Receive, Set Feet, Pass, Reset. Ready on two tracks. Hold for 3, 2, 1.',
      },
      { time: 1448, text: 'Partner toss to the middle. Eyes on the ball.' },
      { time: 1456, text: 'Pass to chest. Tiny recovery step. Reset.' },
      { time: 1464, text: 'Beat. Ready, pass, reset. Keep it smooth.' },
      { time: 1472, text: 'Platform strong. Thumbs together, wrists flat.' },
      { time: 1480, text: 'Aim small. Sticker spot on the shirt.' },
      { time: 1488, text: 'Feet quiet. Toes and knees line up. Tall spine.' },
      { time: 1496, text: 'Switch every eight passes. Count together.' },
      { time: 1504, text: 'Breathe out on contact. Head over hips.' },
      { time: 1512, text: 'Micro shuffle if needed, then pass.' },
      {
        time: 1520,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 1528, text: 'Pulse. Three tiny knee bends. Hold for 3, 2, 1.' },
      { time: 1536, text: 'Count clean ten in a row. Calm and smooth.' },
      { time: 1544, text: 'Last round. Small steps, soft touch.' },

      // Short flow + cooldown 1620–1800
      {
        time: 1620,
        text: 'Short flow. Three in a row. Kneeling pass, ready pass, micro shuffle pass. Go.',
      },
      {
        time: 1638,
        text: 'Switch roles. Repeat the same flow. Kneeling, ready, shuffle.',
      },
      { time: 1656, text: 'Clean six flow reps together. Smooth and quiet.' },
      {
        time: 1674,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 1692,
        text: 'Forearm stretch. Palm down, then palm up. Hold for 3, 2, 1.',
      },
      { time: 1710, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 1728,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1.',
      },
      { time: 1746, text: 'Team check. Quiet high fives.' },
      { time: 1764, text: 'Great work. See you next time!' },
    ],
    timeTotal: 1800,
    equipment:
      'Foam volleyball (youth no-sting), partner; Optional: floor tape dots or small cones for spots; kneeling pad',
  },

  // INTERMEDIATE PARTNER PASSING (45 minutes) Done
  {
    id: 2081,
    name: 'Intermediate Partner Passing',
    categories: ['Passing', 'intermediate', 'partner'],
    sports: ['volleyball'],
    difficulty: 'intermediate',
    skill: 'Passing',
    points: 45,
    instructions:
      'Indoor and small backyard friendly. Foam volleyball, quiet feet, short distance. Clear a small area, check the ceiling, and move breakables. Stand six to ten feet apart or kneel if space is tight. Chest high arcs only. Keep two tracks, knees over toes, tall spine, thumbs together, wrists flat, shoulders forward, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–120 (movement starts right away)
      {
        time: 0,
        text: 'Ready stance on two tracks. Knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Platform shape. Thumbs together, wrists flat, straight arms, shoulders forward.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Big arm swings, small range. Ribs down.' },
      { time: 48, text: 'Wrist circles, ten each way.' },
      {
        time: 60,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 72,
        text: 'Platform taps. Tap the ball gently up. Arms stay straight.',
      },
      {
        time: 84,
        text: 'Angle rehearsal. Point platform left, right, then middle. Small moves.',
      },
      {
        time: 96,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      { time: 108, text: 'Reminder. Two tracks, knees over toes, tall spine.' },

      // Stance 1: Stationary target passing 120–360
      {
        time: 120,
        text: 'Stance 1. Stationary Target Passing. Platform set to middle. Hold for 3, 2, 1.',
      },
      {
        time: 138,
        text: 'Partner underhand toss to belly button height. Pass to the chest.',
      },
      {
        time: 156,
        text: 'Lift from your legs. Arms stay straight. Breathe out on the pass.',
      },
      { time: 174, text: 'Aim small. Pick a sticker spot on the shirt.' },
      { time: 192, text: 'Feet quiet. Toes and knees line up on two tracks.' },
      { time: 210, text: 'Switch every six passes. Count together.' },
      {
        time: 228,
        text: 'Pulse. Three tiny knee bends, then one smooth pass.',
      },
      {
        time: 246,
        text: 'Reset shape. Thumbs together, wrists flat. Hold for 3, 2, 1.',
      },
      { time: 264, text: 'Cues. Shoulders forward, eyes on the ball.' },
      { time: 282, text: 'Count clean ten in a row as a team.' },
      { time: 300, text: 'Breathe steady. Tall spine.' },
      { time: 318, text: 'Last round here. Soft touch, calm finish.' },

      // Stance 2: Micro shuffle left and right 360–720
      {
        time: 360,
        text: 'Stance 2. Micro Shuffle Pass. One small shuffle to line up. Hold for 3, 2, 1.',
      },
      {
        time: 378,
        text: 'Shuffle left one step, stop, platform set, pass to chest.',
      },
      {
        time: 396,
        text: 'Shuffle right one step, stop, platform set, pass to chest.',
      },
      {
        time: 414,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      {
        time: 432,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 450, text: 'Switch every four passes. Keep the rhythm.' },
      {
        time: 468,
        text: 'Pulse. Three shuffle and set reps with no ball. Hands ready.',
      },
      { time: 486, text: 'Aim small. Hit the sticker spot.' },
      { time: 504, text: 'Count clean eight in a row with smooth stops.' },
      { time: 522, text: 'Breathe out on the pass. Tall spine.' },
      { time: 540, text: 'Last two reps here. Soft hands.' },

      // Stance 3: Forward and back adjust 720–1080
      {
        time: 720,
        text: 'Stance 3. Forward and Back Adjust. Meet short tosses, tiny retreat for deeper tosses. Hold for 3, 2, 1.',
      },
      {
        time: 738,
        text: 'Short toss. Step in, platform set, pass to chest. Call short.',
      },
      {
        time: 756,
        text: 'Deep toss. One small step back, platform set, pass to chest. Call deep.',
      },
      { time: 774, text: 'Beat cue. Step, set, pass. One, two, three.' },
      { time: 792, text: 'Toes and knees line up. Head over hips.' },
      { time: 810, text: 'Lift from legs. Arms straight. Shoulders forward.' },
      {
        time: 828,
        text: 'Switch every six passes. Count six short and six deep.',
      },
      {
        time: 846,
        text: 'Pulse. Two quick step in and step back reps with no ball.',
      },
      { time: 864, text: 'Aim small. Sticker spot every time.' },
      { time: 882, text: 'Breathe steady. Calm shoulders.' },

      // Stance 4: Micro drop step and set 1080–1440
      {
        time: 1080,
        text: 'Stance 4. Micro Drop Step and Set. Tiny drop step to center the ball, then pass. Hold for 3, 2, 1.',
      },
      {
        time: 1098,
        text: 'Off center toss. Drop step, set on two tracks, pass to chest.',
      },
      { time: 1116, text: 'Switch every four passes. Stay balanced.' },
      {
        time: 1134,
        text: 'Foot angles. Front toe points where you set. Knees over toes.',
      },
      { time: 1152, text: 'Lift from legs. Arms stay straight. Quiet heels.' },
      { time: 1170, text: 'Count clean eight in a row. Calm rhythm.' },
      {
        time: 1188,
        text: 'Pulse. Two quick drop step and set reps with no ball.',
      },
      { time: 1206, text: 'Breathe out on contact. Head stays over hips.' },
      { time: 1224, text: 'Aim small. Sticker spot on the shirt.' },
      { time: 1242, text: 'Last round here. Smooth drop, strong platform.' },

      // Stance 5: Angle redirect passing 1440–2040 (denser real-time cues)
      {
        time: 1440,
        text: 'Stance 5. Angle Redirect Passing. Ready on two tracks. Hold for 3, 2, 1.',
      },
      { time: 1448, text: 'Partner toss to the middle. Eyes on the ball.' },
      { time: 1456, text: 'Pass to partner chest. Tiny recovery step. Reset.' },
      {
        time: 1464,
        text: 'Beat. Ready, set, pass, reset. One, two, three, four.',
      },
      { time: 1472, text: 'Platform strong. Thumbs together, wrists flat.' },
      {
        time: 1480,
        text: 'Angle left. Same toss, change platform to send left. Call left.',
      },
      {
        time: 1488,
        text: 'Angle right. Same toss, change platform to send right. Call right.',
      },
      {
        time: 1496,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 1504, text: 'Switch every six passes. Count together.' },
      { time: 1512, text: 'Aim small. Hit the sticker spot.' },
      { time: 1520, text: 'Breathe out on contact. Tall spine.' },
      { time: 1528, text: 'Pulse. Three tiny knee bends. Hold for 3, 2, 1.' },
      {
        time: 1536,
        text: 'Count clean two full patterns. Middle, left, right, middle.',
      },
      { time: 1544, text: 'Last round here. Small angles, soft pass.' },

      // Accuracy Circuit 1 2040–2340
      {
        time: 2040,
        text: 'Accuracy circuit. Three in a row. Step in pass, shuffle left pass, shuffle right pass. Go.',
      },
      { time: 2060, text: 'Switch roles. Repeat the same three pass pattern.' },
      {
        time: 2080,
        text: 'Score one point for chest hits. Aim for eight points together.',
      },
      { time: 2100, text: 'Breathe and reset between passes. Tall spine.' },
      { time: 2120, text: 'Repeat the circuit. Smooth stops, quiet landings.' },
      { time: 2140, text: 'Aim small. Sticker spot every pass.' },
      { time: 2160, text: 'Finish with six perfect passes each.' },

      // Accuracy Circuit 2 2340–2580
      {
        time: 2340,
        text: 'Target pattern. Middle, left, right, middle. Four pass pattern.',
      },
      { time: 2360, text: 'Switch roles. Repeat the same pattern.' },
      {
        time: 2380,
        text: 'Add one tiny retreat on the deep toss if needed, then pass to chest.',
      },
      { time: 2400, text: 'Count two perfect patterns in a row. No drops.' },
      { time: 2420, text: 'Calm breath. Shoulders relax. Arms straight.' },
      {
        time: 2440,
        text: 'If outdoors and safe, one small step back. Indoors, keep distance.',
      },
      { time: 2460, text: 'Final round. Six calm perfect passes each.' },

      // Cooldown 2580–2700
      {
        time: 2580,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2610,
        text: 'Forearm stretch. Palm down, then palm up. Hold for 3, 2, 1.',
      },
      { time: 2640, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 2670,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1.Good job and great session! See you next time!',
      },
    ],
    timeTotal: 2678,
    equipment:
      'Foam volleyball (youth no-sting), partner; Optional: floor tape dots or small cones for spots; wall target box at chest height; kneeling pad',
  },

  // ADVANCED PARTNER PASSING (45 minutes) Done
  {
    id: 2082,
    name: 'Advanced Partner Passing',
    categories: ['Passing', 'advanced', 'partner'],
    sports: ['volleyball'],
    difficulty: 'advanced',
    skill: 'Passing',
    points: 45,
    instructions:
      'Indoor and small backyard friendly. Foam volleyball, quiet feet, short distance. Clear a small area, check the ceiling, and move breakables. Stand six to ten feet apart or kneel if space is tight. Chest high arcs only. Keep two tracks, knees over toes, tall spine, thumbs together, wrists flat, shoulders forward, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–120 (movement starts right away)
      {
        time: 0,
        text: 'Ready stance on two tracks. Knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Platform shape. Thumbs together, wrists flat, straight arms, shoulders forward.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      {
        time: 48,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 60,
        text: 'Platform taps. Tap the ball gently up. Arms stay straight.',
      },
      {
        time: 72,
        text: 'Hip hinge. Hips back a little, chest tall, knees over toes. Hold for 3, 2, 1.',
      },
      {
        time: 84,
        text: 'Angle rehearsal. Point platform left, right, then middle. Small moves.',
      },
      {
        time: 96,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      { time: 108, text: 'Reminder. Two tracks, knees over toes, tall spine.' },

      // Stance 1: Precision targets 120–360
      {
        time: 120,
        text: 'Stance 1. Precision Targets. Platform set to middle. Hold for 3, 2, 1.',
      },
      {
        time: 132,
        text: 'Partner underhand toss to belly button height. Pass to the chest. Call chest.',
      },
      { time: 144, text: 'Same toss. Pass to left shoulder. Call left.' },
      { time: 156, text: 'Same toss. Pass to right shoulder. Call right.' },
      {
        time: 168,
        text: 'Lift from legs. Arms straight. Breathe out as you pass.',
      },
      { time: 180, text: 'Aim small. Pick a sticker spot on the shirt.' },
      { time: 192, text: 'Feet quiet. Toes and knees line up on two tracks.' },
      {
        time: 204,
        text: 'Pulse. Three tiny knee bends, then one smooth pass.',
      },
      { time: 216, text: 'Switch every six passes. Count patterns together.' },
      {
        time: 228,
        text: 'Reset shape. Thumbs together, wrists flat. Hold for 3, 2, 1.',
      },
      {
        time: 240,
        text: 'Count clean two full patterns. Middle, left, right, middle.',
      },
      { time: 252, text: 'Last round here. Calm rhythm, soft touch.' },

      // Stance 2: Reaction calls with micro shuffle 360–720
      {
        time: 360,
        text: 'Stance 2. Reaction Calls. One small shuffle to line up. Hold for 3, 2, 1.',
      },
      {
        time: 372,
        text: 'Partner calls middle, left, or right as they toss. Pass to the called target.',
      },
      {
        time: 384,
        text: 'Shuffle left one step, stop, platform set, pass. Call left.',
      },
      {
        time: 396,
        text: 'Shuffle right one step, stop, platform set, pass. Call right.',
      },
      {
        time: 408,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 420, text: 'Beat cue. Shuffle, set, pass. One, two, three.' },
      { time: 432, text: 'Switch every six passes. Breathe out on contact.' },
      { time: 444, text: 'Aim small. Sticker spot every pass.' },
      { time: 456, text: 'Count clean nine reaction passes. Calm rhythm.' },
      { time: 468, text: 'Reminder. Tall spine, head over hips.' },

      // Stance 3: Short in and tiny retreat 720–1080
      {
        time: 720,
        text: 'Stance 3. Short In and Tiny Retreat. Meet short tosses, tiny retreat for deep tosses. Hold for 3, 2, 1.',
      },
      {
        time: 732,
        text: 'Short toss. Step in, platform set, pass to chest. Call short.',
      },
      {
        time: 744,
        text: 'Deep toss. One small step back, platform set, pass to chest. Call deep.',
      },
      { time: 756, text: 'Toes and knees line up. Head over hips.' },
      { time: 768, text: 'Lift from legs. Arms straight. Shoulders forward.' },
      {
        time: 780,
        text: 'Switch every six passes. Count six short and six deep.',
      },
      {
        time: 792,
        text: 'Pulse. Two quick step in and step back reps with no ball.',
      },
      { time: 804, text: 'Aim small. Sticker spot every time.' },
      { time: 816, text: 'Breathe steady. Calm shoulders.' },
      { time: 828, text: 'Last round. Smooth adjust, soft pass.' },

      // Stance 4: Micro drop step and angle 1080–1440
      {
        time: 1080,
        text: 'Stance 4. Micro Drop Step and Angle. Tiny drop step to center the ball, then angle pass. Hold for 3, 2, 1.',
      },
      {
        time: 1092,
        text: 'Off center toss. Drop step, set on two tracks, pass to chest.',
      },
      {
        time: 1104,
        text: 'Same toss. Change platform angle to send left. Call left.',
      },
      {
        time: 1116,
        text: 'Same toss. Change platform angle to send right. Call right.',
      },
      { time: 1128, text: 'Front toe points where you set. Knees over toes.' },
      { time: 1140, text: 'Lift from legs. Quiet heels. Breathe out on pass.' },
      {
        time: 1152,
        text: 'Switch every six passes. Count two clean patterns.',
      },
      { time: 1164, text: 'Reset posture. Platform strong. Hold for 3, 2, 1.' },
      { time: 1176, text: 'Last round here. Smooth drop, strong platform.' },

      // Stance 5: On the move redirect 1440–2040 (dense real-time cues)
      {
        time: 1440,
        text: 'Stance 5. On the Move Redirect. Ready on two tracks. Hold for 3, 2, 1.',
      },
      { time: 1448, text: 'Partner toss to the middle. Eyes on the ball.' },
      { time: 1456, text: 'Pass to chest. Tiny recovery step. Reset.' },
      {
        time: 1464,
        text: 'Beat. Move, set, pass, reset. One, two, three, four.',
      },
      { time: 1472, text: 'Platform strong. Thumbs together, wrists flat.' },
      {
        time: 1480,
        text: 'Partner toss left lane. Shuffle left, stop, pass to chest.',
      },
      {
        time: 1488,
        text: 'Partner toss right lane. Shuffle right, stop, pass to chest.',
      },
      {
        time: 1496,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 1504, text: 'Switch every six passes. Count together.' },
      { time: 1512, text: 'Aim small. Hit the sticker spot.' },
      { time: 1520, text: 'Breathe out on contact. Tall spine.' },
      {
        time: 1528,
        text: 'Call middle on center. Call left or right on angles.',
      },
      { time: 1536, text: 'Pulse. Three tiny knee bends. Hold for 3, 2, 1.' },
      { time: 1544, text: 'Count clean nine moving redirects.' },
      { time: 1552, text: 'Last round here. Small steps, soft touch.' },

      // Accuracy Circuit 1 2040–2340
      {
        time: 2040,
        text: 'Accuracy circuit. Three in a row. Short in pass, shuffle left pass, shuffle right pass. Go.',
      },
      { time: 2060, text: 'Switch roles. Repeat the same three pass pattern.' },
      {
        time: 2080,
        text: 'Score one point for chest hits. Aim for eight points together.',
      },
      { time: 2100, text: 'Breathe and reset between passes. Tall spine.' },
      { time: 2120, text: 'Repeat the circuit. Smooth stops, quiet landings.' },
      { time: 2140, text: 'Aim small. Sticker spot every pass.' },
      { time: 2160, text: 'Finish with six perfect passes each.' },

      // Speed Circuit 2 2340–2580 (no long silent gaps)
      {
        time: 2340,
        text: 'Speed circuit. Ten passes on a steady beat. Ready, pass, reset. One, two.',
      },
      { time: 2360, text: 'Switch roles. Ten passes on the beat.' },
      {
        time: 2380,
        text: 'Add reaction call. Partner says left, right, or middle before toss.',
      },
      { time: 2400, text: 'Count two perfect rounds each. No drops.' },
      { time: 2420, text: 'Calm breath. Shoulders relax. Arms straight.' },
      {
        time: 2440,
        text: 'If outdoors and safe, one small step back. Indoors, keep distance.',
      },
      { time: 2460, text: 'Final round. Six calm perfect passes each.' },
      { time: 2480, text: 'Count aloud. One, two, three, four.' },
      { time: 2500, text: 'Breathe and reset. Tall spine.' },
      { time: 2520, text: 'Last two passes each. Soft touch.' },
      { time: 2540, text: 'Team check. Quiet high fives.' },
      { time: 2560, text: 'Reset space. Sip of water if needed.' },

      // Cooldown 2580–2700
      {
        time: 2580,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2610,
        text: 'Forearm stretch. Palm down, then palm up. Hold for 3, 2, 1.',
      },
      { time: 2640, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 2670,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1.',
      },
      { time: 2690, text: 'Great work. Team check. Quiet high fives.' },
    ],
    timeTotal: 2700,
    equipment:
      'Foam volleyball (youth no-sting), partner; Optional: floor tape dots or small cones for spots; wall target box at chest height; shirt sticker targets; kneeling pad',
  },
  // BEGINNER PARTNER SETTING (30 minutes) Done
  {
    id: 2083,
    name: 'Beginner Partner Setting',
    categories: ['Setting', 'beginner', 'partner'],
    sports: ['volleyball'],
    difficulty: 'beginner',
    skill: 'Setting',
    points: 30,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, check the ceiling, and move breakables. Stand six to eight feet apart or kneel if space is tight. Sets stay around forehead height. Keep two tracks, knees over toes, tall spine, diamond window, soft finger pads, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–216 (movement starts right away)
      {
        time: 0,
        text: 'Ready stance on two tracks. Knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Hands make a diamond window above your forehead. Finger pads ready.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      {
        time: 48,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 60,
        text: 'Air taps. Soft taps from the forehead zone with finger pads.',
      },
      { time: 72, text: 'Reminder. Two tracks, knees over toes, tall spine.' },
      { time: 84, text: 'Ten soft self sets to the window. Light touch.' },
      { time: 96, text: 'Elbows out. Shoulders relaxed.' },
      {
        time: 108,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      { time: 120, text: 'Diamond window again. Hold for 3, 2, 1.' },
      { time: 132, text: 'Finger pads do the work. No slap.' },
      { time: 144, text: 'Reset posture. Head over hips. Feet quiet.' },
      {
        time: 168,
        text: 'Two practice catches at the window, then gentle set up.',
      },
      { time: 192, text: 'Last warm up cue. Smooth push, soft finish.' },
      { time: 216, text: 'Get ready for partner tosses.' },

      // Stance 1: Stationary partner set 240–438
      {
        time: 240,
        text: 'Stance 1. Stationary Partner Set. Hands high in the window. Hold for 3, 2, 1.',
      },
      {
        time: 258,
        text: 'Partner underhand toss to the forehead zone. Set back to the chest.',
      },
      {
        time: 276,
        text: 'Lift from your legs. Arms stay in front. Breathe out on the set.',
      },
      { time: 294, text: 'Aim small. Pick a sticker spot on the shirt.' },
      { time: 312, text: 'Feet quiet. Toes and knees line up on two tracks.' },
      { time: 330, text: 'Switch every five sets. Count together.' },
      { time: 348, text: 'Pulse. Three tiny knee bends, then one smooth set.' },
      {
        time: 366,
        text: 'Reset shape. Diamond window, finger pads. Hold for 3, 2, 1.',
      },
      { time: 384, text: 'Count clean eight in a row as a team.' },
      { time: 402, text: 'Breathe steady. Tall spine.' },
      { time: 420, text: 'Last two sets in this stance. Soft hands.' },
      { time: 438, text: 'Transition. Quick shake out.' },

      // Stance 2: Kneeling partner set 540–840
      {
        time: 540,
        text: 'Stance 2. Kneeling Partner Set. Both knees down, tall spine. Hold for 3, 2, 1.',
      },
      {
        time: 558,
        text: 'Partner soft toss to the window. Set back gently to the chest.',
      },
      { time: 576, text: 'Finger pads press and release. Wrists stay calm.' },
      { time: 594, text: 'Breathe out on the set. Quiet knees.' },
      { time: 612, text: 'Switch every five sets. Count together.' },
      { time: 630, text: 'Angle check. Hands stay in the window. Elbows out.' },
      {
        time: 648,
        text: 'Pulse. Three pretend sets with no ball. Feel the path.',
      },
      { time: 666, text: 'Count clean eight kneeling sets together.' },
      { time: 684, text: 'Reset shape. Diamond window. Hold for 3, 2, 1.' },
      { time: 702, text: 'Last round kneeling. Smooth toss, smooth set.' },
      { time: 720, text: 'Transition. One foot up. Stand slowly. Quiet feet.' },

      // Stance 3: One knee angle set 840–1140
      {
        time: 840,
        text: 'Stance 3. One Knee Angle Set. Setting side knee down, other foot forward on two tracks. Hold for 3, 2, 1.',
      },
      {
        time: 858,
        text: 'Partner toss slightly left. Turn shoulders a little, set to the chest.',
      },
      {
        time: 876,
        text: 'Partner toss slightly right. Turn shoulders a little, set to the chest.',
      },
      {
        time: 894,
        text: 'Front toe points to partner. Small shift if needed.',
      },
      { time: 912, text: 'Switch every five sets. Count together.' },
      { time: 930, text: 'Cues. Diamond window, finger pads, lift from legs.' },
      { time: 948, text: 'Count clean six left and six right as a team.' },
      {
        time: 966,
        text: 'Pulse. Two quick hold and release sets each side. Hold for 3, 2, 1.',
      },
      { time: 984, text: 'Breathe steady. Head over hips.' },
      { time: 1002, text: 'Last round on one knee. Smooth angle, soft set.' },
      { time: 1020, text: 'Transition. Stand up. Shake out arms.' },

      // Stance 4: Micro shuffle set left and right 1140–1440
      {
        time: 1140,
        text: 'Stance 4. Micro Shuffle Set. One small shuffle to line up, then set to the chest. Hold for 3, 2, 1.',
      },
      {
        time: 1158,
        text: 'Shuffle left one step, stop, hands high, set. Partner two hand catch.',
      },
      { time: 1176, text: 'Shuffle right one step, stop, hands high, set.' },
      { time: 1194, text: 'Switch every four sets. Keep the rhythm.' },
      {
        time: 1212,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      {
        time: 1230,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 1248, text: 'Count clean six each side with smooth stops.' },
      { time: 1266, text: 'Aim small. Hit the sticker spot.' },
      { time: 1284, text: 'Breathe out on the set. Tall spine.' },
      {
        time: 1302,
        text: 'Last round in this stance. Small steps, soft hands.',
      },

      // Stance 5: Receive, set, reset 1440–1620 (denser real-time cues)
      {
        time: 1440,
        text: 'Stance 5. Receive, Set, Reset. Ready on two tracks. Hold for 3, 2, 1.',
      },
      {
        time: 1448,
        text: 'Hands high in the diamond window. Finger pads ready.',
      },
      { time: 1456, text: 'Partner toss to the window. Eyes on the ball.' },
      { time: 1464, text: 'Set to the chest. Breathe out on contact.' },
      { time: 1472, text: 'Tiny recovery step. Reset to ready.' },
      { time: 1480, text: 'Beat. Ready, set, reset. Keep it smooth.' },
      { time: 1488, text: 'Thumbs and index make the window. Elbows out.' },
      {
        time: 1496,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 1504, text: 'Switch every eight sets. Count together.' },
      { time: 1512, text: 'Hands stay high. Tall spine. Head over hips.' },
      { time: 1520, text: 'Aim small. Hit the sticker spot.' },
      { time: 1528, text: 'Micro shuffle if needed, then set.' },
      { time: 1536, text: 'Breathe steady. Soft touch.' },
      { time: 1544, text: 'Call middle, then set to chest.' },
      {
        time: 1552,
        text: 'Call left, soft set left. Then right, soft set right.',
      },
      { time: 1560, text: 'Pulse. Three tiny knee bends. Hold for 3, 2, 1.' },
      { time: 1568, text: 'Count clean ten in a row. Calm and smooth.' },
      { time: 1600, text: 'Last round. Quiet feet, strong finish.' },

      // Short flow + cooldown 1620–1800
      {
        time: 1620,
        text: 'Short flow. Three in a row. Kneeling set, ready set, micro shuffle set. Go.',
      },
      {
        time: 1638,
        text: 'Switch roles. Repeat the same flow. Kneeling, ready, shuffle.',
      },
      { time: 1656, text: 'Clean six flow reps together. Smooth and quiet.' },
      {
        time: 1674,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 1692,
        text: 'Forearm and finger stretch. Palms down, then palms up. Hold for 3, 2, 1.',
      },
      { time: 1710, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 1728,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1... Team check. Quiet high fives. Great work!',
      },
    ],
    timeTotal: 1734,
    equipment:
      'Foam volleyball (youth no-sting), partner; Optional: wall target at forehead height; floor tape dots or small cones for spots; kneeling pad',
  },

  // INTERMEDIATE PARTNER SETTING (45 minutes) Done
  {
    id: 2084,
    name: 'Intermediate Partner Setting',
    categories: ['Setting', 'intermediate', 'partner'],
    sports: ['volleyball'],
    difficulty: 'intermediate',
    skill: 'Setting',
    points: 45,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, check the ceiling, and move breakables. Stand six to ten feet apart or kneel if space is tight. Sets stay around forehead height. Keep two tracks, knees over toes, tall spine, diamond window, soft finger pads, light weight split on the balls of the feet, and breathe on effort. Timed instructions start directly with movement at time zero.',
    timedInstructions: [
      // Warm up 0–240 (movement starts right away)
      {
        time: 0,
        text: 'Ready stance on two tracks. Knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Hands make a diamond window above your forehead. Finger pads ready.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Big arm swings, small range. Ribs down.' },
      { time: 48, text: 'Wrist circles, ten each way.' },
      {
        time: 60,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 72,
        text: 'Air taps. Soft taps from the forehead zone with finger pads.',
      },
      { time: 84, text: 'Reminder. Two tracks, knees over toes, tall spine.' },
      { time: 96, text: 'Wall or partner target check. Aim chest height.' },
      {
        time: 108,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      {
        time: 120,
        text: 'Diamond window. Thumbs and index make the window. Hold for 3, 2, 1.',
      },
      { time: 132, text: 'Elbows out. Shoulders relaxed.' },
      { time: 144, text: 'Ten soft self sets to the window. Light touch.' },
      { time: 168, text: 'Reset posture. Head over hips. Feet quiet.' },
      {
        time: 192,
        text: 'Two practice catches at the window, then gentle set up.',
      },
      {
        time: 216,
        text: 'Last warm up cue. Finger pads do the work. Smooth push.',
      },

      // Stance 1: Stationary precision sets 240–600
      {
        time: 240,
        text: 'Stance 1. Stationary Precision Sets. Hands high in the window. Hold for 3, 2, 1.',
      },
      {
        time: 258,
        text: 'Partner underhand toss to the forehead zone. Set back to the chest.',
      },
      {
        time: 276,
        text: 'Lift from your legs. Arms stay in front. Breathe out on the set.',
      },
      { time: 294, text: 'Aim small. Pick a sticker spot on the shirt.' },
      { time: 312, text: 'Feet quiet. Toes and knees line up on two tracks.' },
      { time: 330, text: 'Switch every six sets. Count together.' },
      { time: 348, text: 'Pulse. Three tiny knee bends, then one smooth set.' },
      {
        time: 366,
        text: 'Reset shape. Diamond window, finger pads. Hold for 3, 2, 1.',
      },
      { time: 384, text: 'Count clean ten in a row as a team.' },
      { time: 402, text: 'Last round here. Calm and steady.' },

      // Stance 2: Micro shuffle set left and right 600–960
      {
        time: 600,
        text: 'Stance 2. Micro Shuffle Set. One small shuffle to line up. Hold for 3, 2, 1.',
      },
      {
        time: 618,
        text: 'Shuffle left one step, stop, hands high, set to chest.',
      },
      {
        time: 636,
        text: 'Shuffle right one step, stop, hands high, set to chest.',
      },
      {
        time: 654,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      {
        time: 672,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 690, text: 'Switch every four sets. Keep the rhythm.' },
      {
        time: 708,
        text: 'Pulse. Three shuffle and set reps with no ball. Hands stay ready.',
      },
      { time: 726, text: 'Aim small. Hit the sticker spot.' },
      { time: 744, text: 'Count clean eight in a row with smooth stops.' },
      { time: 762, text: 'Breathe out on the set. Tall spine.' },
      { time: 780, text: 'Last two reps in this stance. Soft hands.' },

      // Stance 3: Forward and back adjust sets 960–1320
      {
        time: 960,
        text: 'Stance 3. Forward and Back Adjust. Meet short tosses, tiny retreat for deep tosses. Hold for 3, 2, 1.',
      },
      {
        time: 978,
        text: 'Short toss. Step in, hands high, set to chest. Call short.',
      },
      {
        time: 996,
        text: 'Deep toss. One small step back, hands high, set to chest. Call deep.',
      },
      { time: 1014, text: 'Beat cue. Step, set, reset. One, two, three.' },
      { time: 1032, text: 'Toes and knees line up. Head over hips.' },
      {
        time: 1050,
        text: 'Lift from legs. Wrists calm. Finger pads push soft.',
      },
      {
        time: 1068,
        text: 'Switch every six sets. Count six short and six deep.',
      },
      {
        time: 1086,
        text: 'Pulse. Two quick step in and step back reps with no ball.',
      },
      { time: 1104, text: 'Aim small. Sticker spot every time.' },
      { time: 1122, text: 'Breathe steady. Tall spine.' },

      // Stance 4: Micro pivot set and angle redirect 1320–1680
      {
        time: 1320,
        text: 'Stance 4. Micro Pivot and Angle. Tiny pivot on the back foot to square shoulders. Hold for 3, 2, 1.',
      },
      {
        time: 1338,
        text: 'Off center toss. Pivot, set feet on two tracks, set to chest.',
      },
      {
        time: 1356,
        text: 'Same toss. Change hand angle to send left. Call left.',
      },
      {
        time: 1374,
        text: 'Same toss. Change hand angle to send right. Call right.',
      },
      { time: 1392, text: 'Front toe points where you set. Knees over toes.' },
      {
        time: 1410,
        text: 'Lift from legs. Quiet heels. Breathe out on contact.',
      },
      {
        time: 1428,
        text: 'Switch every six sets. Count two full patterns clean.',
      },
      {
        time: 1446,
        text: 'Reset posture. Hands high in the window. Hold for 3, 2, 1.',
      },
      { time: 1464, text: 'Last round here. Smooth pivot, soft touch.' },

      // Stance 5: Receive, quick set, recover 1680–2040 (denser real-time cues)
      {
        time: 1680,
        text: 'Stance 5. Receive, Quick Set, Recover. Ready on two tracks. Hold for 3, 2, 1.',
      },
      {
        time: 1688,
        text: 'Hands high in the diamond window. Finger pads ready.',
      },
      {
        time: 1696,
        text: 'Partner toss to the forehead zone. Eyes on the ball.',
      },
      { time: 1704, text: 'Set to the chest. Breathe out on contact.' },
      { time: 1712, text: 'Tiny recovery step. Reset to ready.' },
      { time: 1720, text: 'Beat. Ready, set, recover, reset. Keep it smooth.' },
      { time: 1728, text: 'Thumbs and index make the window. Elbows out.' },
      {
        time: 1736,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 1744, text: 'Switch every six sets. Count together.' },
      { time: 1752, text: 'Hands stay high. Tall spine. Head over hips.' },
      { time: 1760, text: 'Aim small. Hit the sticker spot.' },
      { time: 1768, text: 'Micro shuffle if needed, then set.' },
      { time: 1776, text: 'Breathe steady. Soft touch.' },
      { time: 1784, text: 'Call middle, then set to chest.' },
      {
        time: 1792,
        text: 'Call left, soft set left. Then right, soft set right.',
      },
      { time: 1800, text: 'Pulse. Three tiny knee bends. Hold for 3, 2, 1.' },
      { time: 1808, text: 'Count clean ten in a row. Calm and smooth.' },
      { time: 1816, text: 'Last round here. Quiet feet, strong finish.' },

      // Accuracy Circuit 1 2040–2340
      {
        time: 2040,
        text: 'Accuracy circuit. Three in a row. Step in set, shuffle left set, shuffle right set. Go.',
      },
      { time: 2060, text: 'Switch roles. Repeat the same three set pattern.' },
      {
        time: 2080,
        text: 'Score one point for chest hits. Aim for eight points together.',
      },
      { time: 2100, text: 'Breathe and reset between sets. Tall spine.' },
      { time: 2120, text: 'Repeat the circuit. Smooth stops, quiet landings.' },
      { time: 2140, text: 'Aim small. Sticker spot every set.' },
      { time: 2160, text: 'Finish with six perfect sets each.' },

      // Accuracy Circuit 2 2340–2580
      {
        time: 2340,
        text: 'Target pattern. Middle, left, right, middle. Four set pattern.',
      },
      { time: 2360, text: 'Switch roles. Repeat the same pattern.' },
      {
        time: 2380,
        text: 'Add one tiny retreat on the deep toss if needed, then set to chest.',
      },
      { time: 2400, text: 'Count two perfect patterns in a row. No drops.' },
      {
        time: 2420,
        text: 'Calm breath. Shoulders relax. Finger pads do the work.',
      },
      {
        time: 2440,
        text: 'If outdoors and safe, one small step back. Indoors, keep distance.',
      },
      { time: 2460, text: 'Final round. Six calm perfect sets each.' },

      // Cooldown 2580–2700
      {
        time: 2580,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2610,
        text: 'Forearm and finger stretch. Palms down, then palms up. Hold for 3, 2, 1.',
      },
      { time: 2640, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 2670,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Great session, see you next time.',
      },
    ],
    timeTotal: 2700,
    equipment:
      'Foam volleyball (youth no-sting), partner; Optional: wall target at forehead height; floor tape dots or small cones for spots; shirt sticker targets; kneeling pad',
  },

  // ADVANCED PARTNER SETTING (45 minutes) Done
  {
    id: 2085,
    name: 'Advanced Partner Setting',
    categories: ['Setting', 'advanced', 'partner'],
    sports: ['volleyball'],
    difficulty: 'advanced',
    skill: 'Setting',
    points: 45,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, check ceiling, move breakables. Stand six to ten feet apart or kneel if space is tight. Sets stay around forehead height. Keep two tracks, knees over toes, tall spine, diamond window, soft finger pads, weight split light on the balls of the feet, and breathe on effort. Timed instructions start directly with movement at time zero.',
    timedInstructions: [
      // Start directly with movement (no space clearing in timed steps)
      {
        time: 0,
        text: 'Ready stance on two tracks. Knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      {
        time: 18,
        text: 'Hands make a diamond window above your forehead. Finger pads ready.',
      },
      { time: 36, text: 'Warm up. Arm circles, small and smooth.' },
      { time: 54, text: 'Wrist circles, ten each way. Breathe steady.' },
      {
        time: 72,
        text: 'Fast feet in place, light and quiet for ten seconds.',
      },
      {
        time: 90,
        text: 'Air taps. Soft taps from the forehead zone with finger pads.',
      },
      { time: 108, text: 'Reminder. Two tracks, knees over toes, tall spine.' },

      // Stance 1: Precision window targets
      {
        time: 120,
        text: 'Stance 1. Precision Window Targets. Hands high in the window. Hold for 3, 2, 1.',
      },
      {
        time: 140,
        text: 'Partner underhand toss to the forehead zone. Set to the chest. Call middle.',
      },
      { time: 158, text: 'Same toss. Set to left shoulder. Call left.' },
      { time: 176, text: 'Same toss. Set to right shoulder. Call right.' },
      {
        time: 194,
        text: 'Cues. Diamond window, finger pads, elbows out, lift from legs.',
      },
      { time: 212, text: 'Foot angles. Toes and knees line up on two tracks.' },
      { time: 230, text: 'Pulse. Three tiny knee bends, then one smooth set.' },
      {
        time: 248,
        text: 'Switch every six sets. Count clean patterns together.',
      },
      { time: 266, text: 'Aim small. Sticker spot. Calm breath.' },

      // Stance 2: Reaction calls with micro shuffle
      {
        time: 300,
        text: 'Stance 2. Reaction Calls. One small shuffle to line up. Hold for 3, 2, 1.',
      },
      {
        time: 318,
        text: 'Partner calls middle, left, or right as they toss. Set to the called target.',
      },
      {
        time: 336,
        text: 'Shuffle left one step, stop, hands high, set. Call left.',
      },
      {
        time: 354,
        text: 'Shuffle right one step, stop, hands high, set. Call right.',
      },
      {
        time: 372,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 390, text: 'Beat cue. Shuffle, set, reset. One, two, three.' },
      { time: 408, text: 'Switch every six sets. Breathe out on contact.' },
      { time: 426, text: 'Count clean nine reaction sets. Calm rhythm.' },
      { time: 444, text: 'Reminder. Tall spine, head over hips.' },

      // Stance 3: Forward and back adjust with quick recover
      {
        time: 480,
        text: 'Stance 3. Forward and Back Adjust. Meet short tosses, tiny retreat for deeper tosses. Hold for 3, 2, 1.',
      },
      {
        time: 498,
        text: 'Short toss. Step in, hands high, set to chest. Call short.',
      },
      {
        time: 516,
        text: 'Deep toss. One small step back, hands high, set to chest. Call deep.',
      },
      {
        time: 534,
        text: 'Beat cue. Step, set, recover, reset. One, two, three, four.',
      },
      { time: 552, text: 'Toes and knees line up. Head over hips.' },
      {
        time: 570,
        text: 'Lift from legs. Wrists calm. Finger pads push soft.',
      },
      {
        time: 588,
        text: 'Switch every six sets. Count six short and six deep.',
      },
      {
        time: 606,
        text: 'Pulse. Two quick step in and step back reps with no ball.',
      },
      { time: 624, text: 'Aim small. Sticker spot. Breathe steady.' },

      // Stance 4: Micro pivot and angle redirect
      {
        time: 660,
        text: 'Stance 4. Micro Pivot and Angle Redirect. Tiny pivot on the back foot to square shoulders. Hold for 3, 2, 1.',
      },
      {
        time: 678,
        text: 'Off center toss. Pivot, set feet on two tracks, set to chest.',
      },
      {
        time: 696,
        text: 'Same toss. Change hand angle to send left. Call left.',
      },
      {
        time: 714,
        text: 'Same toss. Change hand angle to send right. Call right.',
      },
      {
        time: 732,
        text: 'Foot angles. Front toe points where you set. Knees over toes.',
      },
      {
        time: 750,
        text: 'Lift from legs. Quiet heels. Breathe out on contact.',
      },
      {
        time: 768,
        text: 'Switch every six sets. Count two full patterns clean.',
      },
      { time: 786, text: 'Last round here. Smooth pivot, soft touch.' },

      // Stance 5: Quick set on the move (dense real-time cues)
      {
        time: 820,
        text: 'Stance 5. Quick Set on the Move. Ready on two tracks. Hold for 3, 2, 1.',
      },
      {
        time: 828,
        text: 'Hands high in the diamond window. Finger pads ready.',
      },
      {
        time: 836,
        text: 'Partner toss to the forehead zone. Eyes on the ball.',
      },
      { time: 844, text: 'Set to the chest. Breathe out on contact.' },
      { time: 852, text: 'Tiny recovery step. Reset to ready.' },
      { time: 860, text: 'Beat. Ready, set, recover, reset. Keep it smooth.' },
      { time: 868, text: 'Thumbs and index make the diamond. Elbows out.' },
      {
        time: 876,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 884, text: 'Switch after six sets. Partner goes now.' },
      { time: 892, text: 'Hands high. Diamond window. Eyes up.' },
      { time: 900, text: 'Lift from your legs. Wrists stay calm.' },
      { time: 908, text: 'Aim small. Hit the sticker spot on the shirt.' },
      { time: 916, text: 'Call middle. Set to the chest.' },
      { time: 924, text: 'Call left. Turn a little, soft set left.' },
      { time: 932, text: 'Call right. Turn a little, soft set right.' },
      { time: 940, text: 'Toes and knees line up. Head over hips.' },
      { time: 948, text: 'Recover step. Reset to ready stance.' },
      { time: 956, text: 'Breathe in, breathe out. Calm shoulders.' },
      { time: 964, text: 'Pulse. Three tiny knee bends. Hold for 3, 2, 1.' },
      { time: 972, text: 'Count clean six in a row. Smooth hands.' },
      { time: 980, text: 'Switch roles. Other partner sets.' },
      { time: 988, text: 'Steady rhythm. Forehead zone toss, set to chest.' },
      { time: 996, text: 'Hands stay high. Elbows out, finger pads push.' },
      { time: 1004, text: 'Micro shuffle if needed, then set.' },
      { time: 1012, text: 'Aim small. Sticker spot again.' },
      { time: 1020, text: 'Count aloud. One, two, three, four.' },
      { time: 1028, text: 'Relax shoulders. Tall spine.' },
      { time: 1036, text: 'Last round. Ready, set, recover, reset.' },
      { time: 1044, text: 'Two perfect sets in a row. Hold for 3, 2, 1.' },
      { time: 1052, text: 'Finish the last two sets. Quiet feet, soft touch.' },

      // Accuracy Circuit 1
      {
        time: 1080,
        text: 'Accuracy circuit. Three in a row. Short step in set, shuffle left set, shuffle right set. Go.',
      },
      { time: 1100, text: 'Switch roles. Repeat the same three set pattern.' },
      {
        time: 1120,
        text: 'Score one point for chest hits. Aim for eight points together.',
      },
      { time: 1140, text: 'Breathe and reset between sets. Tall spine.' },
      { time: 1160, text: 'Repeat the circuit. Smooth stops, quiet landings.' },
      { time: 1180, text: 'Aim small. Sticker spot every set.' },
      { time: 1200, text: 'Finish with six perfect sets each.' },

      // Speed Circuit 2
      {
        time: 1230,
        text: 'Speed circuit. Ten sets on a steady beat. Ready, set, reset. One, two.',
      },
      { time: 1250, text: 'Switch roles. Ten sets on the beat.' },
      {
        time: 1270,
        text: 'Add reaction call. Partner says left, right, or middle before toss.',
      },
      { time: 1290, text: 'Count two perfect rounds each. No drops.' },
      {
        time: 1310,
        text: 'Calm breath. Shoulders relax. Finger pads do the work.',
      },
      {
        time: 1330,
        text: 'If outdoors and safe, take one small step back. If indoors tight, keep distance.',
      },
      { time: 1350, text: 'Final round. Six calm perfect sets each.' },

      // Cooldown
      {
        time: 1380,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 1400,
        text: 'Forearm and finger stretch. Palms down, then palms up. Hold for 3, 2, 1.',
      },
      { time: 1420, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 1440,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Great session, see you next time.',
      },
    ],
    timeTotal: 1446,
    equipment:
      'Foam volleyball (youth no-sting), partner; Optional: wall target at forehead height; floor tape dots or small cones for spots; shirt sticker targets; kneeling pad',
  },

  // BEGINNER PARTNER SERVING (30 minutes) Done
  {
    id: 2086,
    name: 'Beginner Partner Serving',
    categories: ['Serving', 'beginner', 'partner'],
    sports: ['volleyball'],
    difficulty: 'beginner',
    skill: 'Serving',
    points: 30,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, check the ceiling, and move breakables. Stand eight to twelve feet apart or use six to eight feet or a wall target if space is tight. Use a foam volleyball. Chest to low chest targets only. Keep two tracks, front toe points to the target, tall spine, flat open hand, firm wrist, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–216
      {
        time: 0,
        text: 'Ready Serving Stance. Feet on two tracks, front toe to target, knees soft, tall spine. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Hand setup. Ball on front hand at waist. Strike hand open like a paddle. Wrist firm.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      {
        time: 48,
        text: 'Fast feet in place. Quiet and light for ten seconds.',
      },
      {
        time: 60,
        text: 'Shadow swings. Open hand swings to the target. Three smooth swings.',
      },
      {
        time: 72,
        text: 'Contact spot. Tap the bottom third of the ball. Say flat hand.',
      },
      { time: 84, text: 'Front toe points to target. Head over hips.' },
      {
        time: 96,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      { time: 108, text: 'Two tracks, knees over toes. Hold for 3, 2, 1.' },
      { time: 120, text: 'Three more shadow swings. Smooth and light.' },
      { time: 144, text: 'Reset posture. Shoulders relaxed.' },
      { time: 168, text: 'Last warm up cue. Flat hand, firm wrist.' },
      { time: 192, text: 'Aim chest height. Eyes on the target.' },
      { time: 216, text: 'Get ready to serve.' },

      // Stance 1: Stationary underhand taps 240–438
      {
        time: 240,
        text: 'Stance 1. Stationary Underhand Tap. Ready stance. Hold for 3, 2, 1.',
      },
      {
        time: 258,
        text: 'Tap the ball gently to partner chest or wall box. Path stays low and straight.',
      },
      {
        time: 276,
        text: 'Small step with the front foot. Toe points to target.',
      },
      { time: 294, text: 'Breathe out on contact. Quiet feet.' },
      { time: 312, text: 'Flat hand. Firm wrist. Hit the bottom third.' },
      { time: 330, text: 'Switch every five taps. Count together.' },
      { time: 348, text: 'Pulse. Three shadow swings, then one tap.' },
      { time: 366, text: 'Aim small. Sticker spot on the shirt or taped box.' },
      { time: 384, text: 'Count clean eight taps in a row.' },
      { time: 402, text: 'Last two taps here. Smooth and soft.' },
      { time: 420, text: 'Reset stance. Two tracks, front toe to target.' },
      { time: 438, text: 'Transition. Shake out arms.' },

      // Stance 2: Kneeling underhand serve 540–840
      {
        time: 540,
        text: 'Stance 2. Kneeling Underhand Serve. Both knees down, tall spine. Hold for 3, 2, 1.',
      },
      {
        time: 558,
        text: 'Ball on front hand out front. Strike hand swings up from back hip to target.',
      },
      { time: 576, text: 'Aim chest height to partner or wall. Gentle arc.' },
      { time: 594, text: 'Breathe out on contact. Quiet knees.' },
      { time: 612, text: 'Flat hand. Firm wrist. Bottom third of the ball.' },
      { time: 630, text: 'Switch every five serves. Count together.' },
      { time: 648, text: 'Pulse. Three slow shadow swings with no ball.' },
      { time: 666, text: 'Count clean eight kneeling serves.' },
      { time: 684, text: 'Reset shape. Front hand steady. Hold for 3, 2, 1.' },
      { time: 702, text: 'Last round kneeling. Smooth swing, soft finish.' },
      { time: 720, text: 'Transition. One foot up. Stand slowly. Quiet feet.' },

      // Stance 3: Half step underhand serve 840–1140
      {
        time: 840,
        text: 'Stance 3. Half Step Serve. Ready stance. Step, swing, serve. Hold for 3, 2, 1.',
      },
      { time: 858, text: 'Front toe points to target. Head over hips.' },
      {
        time: 876,
        text: 'No toss. Ball stays still on front hand. Swing your open hand to send it.',
      },
      { time: 894, text: 'Partner two hand catch or hit wall box.' },
      { time: 912, text: 'Breathe and reset between serves. Calm shoulders.' },
      { time: 930, text: 'Switch every five serves. Count together.' },
      { time: 948, text: 'Aim small. Hit the sticker spot.' },
      { time: 966, text: 'Pulse. Two shadow step swings, then one serve.' },
      { time: 984, text: 'Count clean ten serves as a team.' },
      { time: 1002, text: 'Last two serves here. Smooth and steady.' },
      { time: 1020, text: 'Reset posture. Two tracks. Firm wrist.' },
      { time: 1040, text: 'Transition. Quick shake out.' },

      // Stance 4: Micro shuffle aim adjust 1140–1440
      {
        time: 1140,
        text: 'Stance 4. Micro Shuffle Aim. One small shuffle to line up, stop, serve. Hold for 3, 2, 1.',
      },
      {
        time: 1158,
        text: 'Shuffle left one step, stop, serve to chest or wall box.',
      },
      {
        time: 1176,
        text: 'Shuffle right one step, stop, serve to chest or wall box.',
      },
      {
        time: 1194,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      { time: 1212, text: 'Breathe out on contact. Head stays over hips.' },
      { time: 1230, text: 'Switch every four serves. Keep rhythm.' },
      {
        time: 1248,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 1266, text: 'Count clean six each side. Calm and accurate.' },
      { time: 1284, text: 'Aim small. Hit the box.' },
      { time: 1302, text: 'Last round here. Small steps, smooth swing.' },

      // Stance 5: Target ladder 1440–1620 (denser real-time cues)
      {
        time: 1440,
        text: 'Stance 5. Target Ladder. Serve to chest, then left shoulder, then right shoulder, then chest. Hold for 3, 2, 1.',
      },
      {
        time: 1448,
        text: 'Front toe points to target. Flat hand, firm wrist.',
      },
      { time: 1456, text: 'Chest target. Breathe out on contact.' },
      { time: 1464, text: 'Left shoulder target. Smooth swing.' },
      { time: 1472, text: 'Right shoulder target. Quiet feet.' },
      { time: 1480, text: 'Back to chest. Count one pattern.' },
      { time: 1488, text: 'Switch every six serves. Partner next.' },
      { time: 1496, text: 'Aim small. Sticker spot or wall box.' },
      { time: 1504, text: 'Head over hips. Two tracks. Knees soft.' },
      { time: 1512, text: 'Pulse. Three shadow swings. Hold for 3, 2, 1.' },
      { time: 1520, text: 'Start pattern two. Chest, left, right, chest.' },
      { time: 1528, text: 'Breathe and reset between serves.' },
      { time: 1536, text: 'Count clean two full patterns in a row.' },
      { time: 1544, text: 'Last round. Soft and accurate. Quiet feet.' },

      // Short flow + cooldown 1620–1800
      {
        time: 1620,
        text: 'Short flow. Three in a row. Kneeling serve, half step serve, micro shuffle serve. Go.',
      },
      {
        time: 1640,
        text: 'Switch roles. Repeat the same flow. Kneeling, step, shuffle.',
      },
      { time: 1660, text: 'Clean six flow reps together. Smooth and quiet.' },
      {
        time: 1680,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 1700,
        text: 'Forearm and finger stretch. Palms down, then palms up. Hold for 3, 2, 1.',
      },
      { time: 1720, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 1740,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1.',
      },
      { time: 1760, text: 'Team check. Quiet high fives. See you next time!' },
    ],
    timeTotal: 1766,
    equipment:
      'Foam volleyball (youth no-sting), partner; Optional: wall or taped target 8 to 12 feet away; floor tape dots or small cones for spots; laundry basket catch; kneeling pad',
  },

  // INTERMEDIATE PARTNER SERVING (45 minutes) Done
  {
    id: 2087,
    name: 'Intermediate Partner Serving',
    categories: ['Serving', 'intermediate', 'partner'],
    sports: ['volleyball'],
    difficulty: 'intermediate',
    skill: 'Serving',
    points: 45,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, check the ceiling, and move breakables. Stand eight to twelve feet apart, or use six to eight feet or a wall target if space is tight. Use a foam volleyball. Chest to low chest targets only. Keep two tracks, front toe points to the target, tall spine, flat open hand, firm wrist, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–216 (movement starts right away)
      {
        time: 0,
        text: 'Ready Serving Stance. Two tracks, front toe to target, knees soft, tall spine. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Hand setup. Ball on front hand at waist. Strike hand open like a paddle. Wrist firm.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      { time: 48, text: 'Fast feet in place. Quiet and light.' },
      {
        time: 60,
        text: 'Shadow swings. Open hand swings to the target. Three smooth swings.',
      },
      {
        time: 72,
        text: 'Contact spot. Hit the bottom third of the ball. Flat hand.',
      },
      { time: 84, text: 'Front toe points to the target. Head over hips.' },
      {
        time: 96,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      { time: 108, text: 'Two tracks, knees over toes. Hold for 3, 2, 1.' },
      { time: 120, text: 'Three more shadow swings. Smooth and light.' },
      { time: 144, text: 'Reset posture. Shoulders relaxed.' },
      { time: 168, text: 'Flat hand, firm wrist. Eyes on the target.' },
      { time: 192, text: 'Aim chest height. Quiet feet.' },
      { time: 216, text: 'Get ready to serve.' },

      // Stance 1: Stationary underhand precision 240–600
      {
        time: 240,
        text: 'Stance 1. Stationary Precision. Ready stance. Hold for 3, 2, 1.',
      },
      {
        time: 258,
        text: 'Tap serve to partner chest or wall box. Path low and straight.',
      },
      {
        time: 276,
        text: 'Small step with the front foot. Toe points to target.',
      },
      { time: 294, text: 'Breathe out on contact. Quiet feet.' },
      { time: 312, text: 'Flat hand. Firm wrist. Bottom third contact.' },
      { time: 330, text: 'Switch every six serves. Count together.' },
      { time: 348, text: 'Pulse. Three shadow swings, then one serve.' },
      { time: 366, text: 'Aim small. Sticker spot or wall box.' },
      { time: 384, text: 'Count clean ten in a row as a team.' },
      { time: 402, text: 'Reset stance. Two tracks. Hold for 3, 2, 1.' },
      { time: 420, text: 'Last round here. Smooth and accurate.' },
      { time: 438, text: 'Transition. Shake out arms.' },

      // Stance 2: Half step rhythm serve 600–960
      {
        time: 600,
        text: 'Stance 2. Half Step Rhythm. Step, swing, serve. Hold for 3, 2, 1.',
      },
      {
        time: 618,
        text: 'Front toe to target. Head over hips. Ball stays still on front hand.',
      },
      { time: 636, text: 'Swing your open hand to send it. No toss.' },
      { time: 654, text: 'Beat. Step, swing. One, two.' },
      { time: 672, text: 'Breathe and reset between serves.' },
      { time: 690, text: 'Switch every six serves. Count together.' },
      { time: 708, text: 'Aim small. Hit the sticker spot.' },
      { time: 726, text: 'Pulse. Two shadow step swings, then one serve.' },
      { time: 744, text: 'Firm wrist at contact. Finish to target.' },
      { time: 762, text: 'Count clean ten as a team. Calm rhythm.' },
      { time: 780, text: 'Last two serves here. Smooth path.' },
      { time: 798, text: 'Transition. Quick shake out.' },

      // Stance 3: Micro shuffle aim adjust 960–1320
      {
        time: 960,
        text: 'Stance 3. Micro Shuffle Aim. One small shuffle to line up, stop, serve. Hold for 3, 2, 1.',
      },
      {
        time: 978,
        text: 'Shuffle left one step, stop, serve to chest or wall box.',
      },
      {
        time: 996,
        text: 'Shuffle right one step, stop, serve to chest or wall box.',
      },
      {
        time: 1014,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      {
        time: 1032,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 1050, text: 'Switch every four serves. Keep rhythm.' },
      { time: 1068, text: 'Breathe out on contact. Head over hips.' },
      { time: 1086, text: 'Aim small. Hit the box. Soft landings.' },
      { time: 1104, text: 'Count clean six each side. Calm and accurate.' },
      { time: 1122, text: 'Pulse. Two shuffle and set reps with no ball.' },
      { time: 1140, text: 'Last round here. Small steps, smooth swing.' },

      // Stance 4: Reaction call targets 1320–1680
      {
        time: 1320,
        text: 'Stance 4. Reaction Calls. Partner calls chest, left, or right. Serve the call. Hold for 3, 2, 1.',
      },
      { time: 1338, text: 'Chest target. Flat hand to middle.' },
      { time: 1356, text: 'Left shoulder target. Smooth swing, firm wrist.' },
      { time: 1374, text: 'Right shoulder target. Quiet feet.' },
      {
        time: 1392,
        text: 'Beat. Ready, call, serve, reset. One, two, three, four.',
      },
      { time: 1410, text: 'Switch every six serves. Count together.' },
      { time: 1428, text: 'Front toe points to target. Head over hips.' },
      { time: 1446, text: 'Aim small. Sticker spots or wall boxes.' },
      {
        time: 1464,
        text: 'Count two perfect patterns. Chest, left, right, chest.',
      },
      { time: 1482, text: 'Last round here. Calm breath.' },
      { time: 1500, text: 'Transition. Reset space. Quick sip if needed.' },

      // Stance 5: Serve, recover, ready 1680–2040 (dense real-time cues)
      {
        time: 1680,
        text: 'Stance 5. Serve, Recover, Ready. Ready on two tracks. Hold for 3, 2, 1.',
      },
      { time: 1688, text: 'Ball still on front hand. Flat hand strike.' },
      { time: 1696, text: 'Serve to chest. Breathe out on contact.' },
      { time: 1704, text: 'Tiny recovery step. Reset to ready.' },
      { time: 1712, text: 'Beat. Ready, serve, recover, reset.' },
      { time: 1720, text: 'Front toe to target. Knees soft.' },
      { time: 1728, text: 'Aim small. Sticker spot.' },
      { time: 1736, text: 'Firm wrist. Finish to target.' },
      { time: 1744, text: 'Switch every six serves. Partner goes now.' },
      { time: 1752, text: 'Quiet feet. Head over hips.' },
      { time: 1760, text: 'Flat hand. Bottom third contact.' },
      { time: 1768, text: 'Count clean ten serves. Calm rhythm.' },
      { time: 1776, text: 'Pulse. Three shadow swings. Hold for 3, 2, 1.' },
      { time: 1784, text: 'Micro shuffle if needed, then serve.' },
      { time: 1792, text: 'Last round. Soft and accurate.' },

      // Accuracy Circuit 1 2040–2340
      {
        time: 2040,
        text: 'Accuracy circuit. Three serves in a row. Chest, left shoulder, right shoulder. Go.',
      },
      {
        time: 2060,
        text: 'Switch roles. Repeat the same three target pattern.',
      },
      {
        time: 2080,
        text: 'Score one point for chest hits. Aim for eight points together.',
      },
      { time: 2100, text: 'Breathe and reset between serves. Tall spine.' },
      { time: 2120, text: 'Repeat the circuit. Smooth steps, quiet landings.' },
      { time: 2140, text: 'Aim small. Sticker spot every serve.' },
      { time: 2160, text: 'Finish with six perfect serves each.' },

      // Speed Circuit 2 2340–2580
      {
        time: 2340,
        text: 'Speed circuit. Ten serves on a steady beat. Ready, serve, reset. One, two.',
      },
      { time: 2360, text: 'Switch roles. Ten serves on the beat.' },
      {
        time: 2380,
        text: 'Add reaction call. Partner says chest, left, or right before serve.',
      },
      { time: 2400, text: 'Count two perfect rounds each. No drops.' },
      { time: 2420, text: 'Calm breath. Shoulders relax. Firm wrist.' },
      {
        time: 2440,
        text: 'If outdoors and safe, one small step back. Indoors, keep distance.',
      },
      { time: 2460, text: 'Final round. Six calm accurate serves each.' },

      // Cooldown 2580–2700
      {
        time: 2580,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2610,
        text: 'Forearm and finger stretch. Palms down, then palms up. Hold for 3, 2, 1.',
      },
      { time: 2640, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 2670,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Great work. See you next time!',
      },
    ],
    timeTotal: 2700,
    equipment:
      'Foam volleyball (youth no-sting), partner; Optional: wall or taped target 8 to 12 feet away; floor tape dots or small cones for spots; laundry basket catch; kneeling pad',
  },

  // ADVANCED PARTNER SERVING (45 minutes) Done
  {
    id: 2088,
    name: 'Advanced Partner Serving',
    categories: ['Serving', 'advanced', 'partner'],
    sports: ['volleyball'],
    difficulty: 'advanced',
    skill: 'Serving',
    points: 45,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, check the ceiling, and move breakables. Stand eight to twelve feet apart, or use six to eight feet or a wall target if space is tight. Use a foam volleyball. Chest to low chest targets only. Keep two tracks, front toe points to the target, tall spine, flat open hand, firm wrist, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–216 (movement starts right away)
      {
        time: 0,
        text: 'Ready Serving Stance. Two tracks, front toe to target, knees soft, tall spine. Hold for 3, 2, 1.',
      },
      {
        time: 12,
        text: 'Hand setup. Ball on front hand at waist. Strike hand open like a paddle. Wrist firm.',
      },
      { time: 24, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      { time: 48, text: 'Fast feet in place. Quiet and light.' },
      {
        time: 60,
        text: 'Shadow swings. Open hand to the target. Three smooth swings.',
      },
      {
        time: 72,
        text: 'Contact spot. Hit the bottom third of the ball. Flat hand.',
      },
      { time: 84, text: 'Front toe points to the target. Head over hips.' },
      {
        time: 96,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      { time: 108, text: 'Two tracks, knees over toes. Hold for 3, 2, 1.' },
      { time: 120, text: 'Three more shadow swings. Smooth and light.' },
      { time: 144, text: 'Reset posture. Shoulders relaxed.' },
      { time: 168, text: 'Flat hand, firm wrist. Eyes on the target.' },
      { time: 192, text: 'Aim chest height. Quiet feet.' },
      { time: 216, text: 'Get ready to serve.' },

      // Stance 1: Precision ladder serve 240–600
      {
        time: 240,
        text: 'Stance 1. Precision Ladder. Serve chest, left shoulder, right shoulder, then chest. Hold for 3, 2, 1.',
      },
      {
        time: 258,
        text: 'Chest target. Breathe out on contact. Path low and straight.',
      },
      {
        time: 276,
        text: 'Left shoulder target. Small step. Front toe points to target.',
      },
      { time: 294, text: 'Right shoulder target. Quiet feet. Firm wrist.' },
      { time: 312, text: 'Back to chest. Count one pattern.' },
      { time: 330, text: 'Switch every six serves. Partner goes now.' },
      { time: 348, text: 'Aim small. Sticker spot or wall box.' },
      { time: 366, text: 'Flat hand. Bottom third contact.' },
      { time: 384, text: 'Beat. Ready, serve, reset. One, two, three.' },
      { time: 402, text: 'Count two perfect patterns in a row.' },
      {
        time: 420,
        text: 'Reset stance. Two tracks, knees soft. Hold for 3, 2, 1.',
      },
      { time: 438, text: 'Last round here. Smooth and accurate.' },
      { time: 456, text: 'Breath cue. In, then out on contact.' },
      { time: 474, text: 'Front toe stays pointed. Head over hips.' },
      { time: 492, text: 'Soft landings. Quiet heels.' },
      { time: 510, text: 'Finish strong. Two clean serves each.' },
      { time: 528, text: 'Transition. Shake out arms. Quick sip if needed.' },

      // Stance 2: Half step rhythm serve 600–960
      {
        time: 600,
        text: 'Stance 2. Half Step Rhythm. Step, swing, serve. Hold for 3, 2, 1.',
      },
      { time: 618, text: 'Ball stays still on front hand. No toss.' },
      { time: 636, text: 'Step then swing. One, two. Finish to target.' },
      { time: 654, text: 'Front toe to target. Tall spine.' },
      { time: 672, text: 'Breathe and reset between serves.' },
      { time: 690, text: 'Switch every six serves. Count together.' },
      { time: 708, text: 'Aim small. Hit the sticker spot.' },
      { time: 726, text: 'Pulse. Two shadow step swings, then one serve.' },
      { time: 744, text: 'Firm wrist at contact. Path stays low.' },
      { time: 762, text: 'Count clean ten as a team. Calm rhythm.' },
      { time: 780, text: 'Last two serves here. Smooth path.' },
      { time: 798, text: 'Transition. Reset stance. Quiet feet.' },

      // Stance 3: Micro shuffle aim adjust 960–1320
      {
        time: 960,
        text: 'Stance 3. Micro Shuffle Aim. One small shuffle to line up, stop, serve. Hold for 3, 2, 1.',
      },
      {
        time: 978,
        text: 'Shuffle left one step, stop, serve to chest or wall box.',
      },
      {
        time: 996,
        text: 'Shuffle right one step, stop, serve to chest or wall box.',
      },
      {
        time: 1014,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      {
        time: 1032,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 1050, text: 'Switch every four serves. Keep rhythm.' },
      { time: 1068, text: 'Breathe out on contact. Head over hips.' },
      { time: 1086, text: 'Aim small. Hit the box. Soft landings.' },
      { time: 1104, text: 'Count clean six each side. Calm and accurate.' },
      { time: 1122, text: 'Pulse. Two shuffle and set reps with no ball.' },
      { time: 1140, text: 'Last round here. Small steps, smooth swing.' },

      // Stance 4: Reaction call targets 1320–1680
      {
        time: 1320,
        text: 'Stance 4. Reaction Calls. Partner calls chest, left, or right. Serve the call. Hold for 3, 2, 1.',
      },
      { time: 1338, text: 'Chest target. Flat hand to middle.' },
      { time: 1356, text: 'Left shoulder target. Smooth swing, firm wrist.' },
      { time: 1374, text: 'Right shoulder target. Quiet feet.' },
      {
        time: 1392,
        text: 'Beat. Ready, call, serve, reset. One, two, three, four.',
      },
      { time: 1410, text: 'Switch every six serves. Count together.' },
      { time: 1428, text: 'Front toe points to target. Head over hips.' },
      { time: 1446, text: 'Aim small. Sticker spots or wall boxes.' },
      {
        time: 1464,
        text: 'Count two perfect patterns. Chest, left, right, chest.',
      },
      { time: 1482, text: 'Last round here. Calm breath.' },
      { time: 1500, text: 'Transition. Reset space. Quick sip if needed.' },

      // Stance 5: Serve, recover, pivot set 1680–2040 (dense real-time cues)
      {
        time: 1680,
        text: 'Stance 5. Serve, Recover, Pivot Set. Ready on two tracks. Hold for 3, 2, 1.',
      },
      { time: 1688, text: 'Ball still on front hand. Flat hand strike.' },
      { time: 1696, text: 'Serve to chest. Breathe out on contact.' },
      {
        time: 1704,
        text: 'Tiny recovery step. Pivot back foot to square. Reset.',
      },
      { time: 1712, text: 'Beat. Ready, serve, recover, reset.' },
      { time: 1720, text: 'Front toe to target. Knees soft.' },
      { time: 1728, text: 'Aim small. Sticker spot.' },
      { time: 1736, text: 'Firm wrist. Finish to target.' },
      { time: 1744, text: 'Switch every six serves. Partner goes now.' },
      { time: 1752, text: 'Quiet feet. Head over hips.' },
      { time: 1760, text: 'Flat hand. Bottom third contact.' },
      { time: 1768, text: 'Count clean ten serves. Calm rhythm.' },
      { time: 1776, text: 'Pulse. Three shadow swings. Hold for 3, 2, 1.' },
      { time: 1784, text: 'Micro shuffle if needed, then serve.' },
      { time: 1792, text: 'Last round. Soft and accurate.' },

      // Accuracy Circuit 1 2040–2340
      {
        time: 2040,
        text: 'Accuracy circuit. Three serves in a row. Chest, left shoulder, right shoulder. Go.',
      },
      {
        time: 2060,
        text: 'Switch roles. Repeat the same three target pattern.',
      },
      {
        time: 2080,
        text: 'Score one point for chest hits. Aim for eight points together.',
      },
      { time: 2100, text: 'Breathe and reset between serves. Tall spine.' },
      { time: 2120, text: 'Repeat the circuit. Smooth steps, quiet landings.' },
      { time: 2140, text: 'Aim small. Sticker spot every serve.' },
      { time: 2160, text: 'Finish with six perfect serves each.' },

      // Speed Circuit 2 2340–2580
      {
        time: 2340,
        text: 'Speed circuit. Ten serves on a steady beat. Ready, serve, reset. One, two.',
      },
      { time: 2360, text: 'Switch roles. Ten serves on the beat.' },
      {
        time: 2380,
        text: 'Add reaction call. Partner says chest, left, or right before serve.',
      },
      { time: 2400, text: 'Count two perfect rounds each. No drops.' },
      { time: 2420, text: 'Calm breath. Shoulders relax. Firm wrist.' },
      {
        time: 2440,
        text: 'If outdoors and safe, one small step back. Indoors, keep distance.',
      },
      { time: 2460, text: 'Final round. Six calm accurate serves each.' },

      // Cooldown 2580–2700
      {
        time: 2580,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2610,
        text: 'Forearm and finger stretch. Palms down, then palms up. Hold for 3, 2, 1.',
      },
      { time: 2640, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 2670,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Great work. See you next time!',
      },
    ],
    timeTotal: 2676,
    equipment:
      'Foam volleyball (youth no-sting), partner; Optional: wall or taped target 8 to 12 feet away; floor tape dots or small cones for spots; laundry basket catch; kneeling pad',
  },
  // COMPLETE PARTNER VOLLEYBALL (60 minutes)
  {
    id: 2089,
    name: 'Complete Partner Volleyball',
    categories: ['Complete', 'advanced', 'partner'],
    sports: ['volleyball'],
    difficulty: 'advanced',
    skill: 'Complete',
    points: 60,
    instructions:
      'Indoor and small backyard friendly. Foam ball, quiet feet, short distance. Clear a small area, check the ceiling, and move breakables. Stand six to twelve feet apart or kneel if space is tight. Chest to forehead height only. Keep two tracks, knees over toes, tall spine, light weight split on the balls of the feet, and breathe on effort. Timed instructions start with movement at time zero.',
    timedInstructions: [
      // Warm up 0–240
      {
        time: 0,
        text: 'Ready stance. Two tracks, knees soft, tall spine, head over hips. Hold for 3, 2, 1.',
      },
      { time: 12, text: 'Arm circles, small and smooth. Breathe steady.' },
      { time: 24, text: 'Big arm swings, small range. Ribs down.' },
      { time: 36, text: 'Wrist circles, ten each way.' },
      { time: 48, text: 'Fast feet in place. Quiet and light.' },
      {
        time: 60,
        text: 'Hip hinge. Hips back a little, chest tall, knees over toes. Hold for 3, 2, 1.',
      },
      {
        time: 72,
        text: 'Breathing check. In through the nose, out through the mouth.',
      },
      {
        time: 90,
        text: 'Platform shape. Thumbs together, wrists flat, arms straight.',
      },
      {
        time: 108,
        text: 'Diamond window. Thumbs and index make a window at the forehead.',
      },
      {
        time: 126,
        text: 'Shadow serve. Open hand, flat contact path. Three smooth swings.',
      },
      {
        time: 144,
        text: 'Foot angles. Toes and knees line up. Light on the balls of the feet.',
      },
      { time: 162, text: 'Ten soft self sets. Finger pads only.' },
      { time: 180, text: 'Five gentle self passes. Lift from legs.' },
      { time: 198, text: 'Two tracks reminder. Tall spine, head over hips.' },
      { time: 216, text: 'Aim chest height. Eyes on the ball.' },
      { time: 234, text: 'Get ready for partner work.' },

      // Stance 1: Passing — Stationary target passing 240–600
      {
        time: 240,
        text: 'Stance 1. Passing. Stationary Target Passing. Platform set to middle. Hold for 3, 2, 1.',
      },
      {
        time: 258,
        text: 'Underhand toss to belly button height. Pass to chest.',
      },
      {
        time: 276,
        text: 'Lift from legs. Arms stay straight. Breathe out on pass.',
      },
      { time: 294, text: 'Aim small. Sticker spot on the shirt.' },
      { time: 312, text: 'Feet quiet. Toes and knees on two tracks.' },
      { time: 330, text: 'Switch every six passes. Count together.' },
      {
        time: 348,
        text: 'Pulse. Three tiny knee bends, then one smooth pass.',
      },
      { time: 366, text: 'Shoulders forward. Eyes on the ball.' },
      { time: 384, text: 'Count clean ten as a team. Calm rhythm.' },
      {
        time: 402,
        text: 'Reset platform. Thumbs together, wrists flat. Hold for 3, 2, 1.',
      },
      { time: 420, text: 'Last round here. Small angle, strong platform.' },
      { time: 438, text: 'Breathe steady. Tall spine.' },
      { time: 456, text: 'Two perfect passes each. Quiet feet.' },
      { time: 480, text: 'Transition. Shake out arms.' },

      // Stance 2: Passing — Micro shuffle redirect 600–960
      {
        time: 600,
        text: 'Stance 2. Passing. Micro Shuffle Redirect. One small shuffle, stop, pass.',
      },
      { time: 618, text: 'Shuffle left, stop, platform set, pass to chest.' },
      { time: 636, text: 'Shuffle right, stop, platform set, pass to chest.' },
      {
        time: 654,
        text: 'Foot angles. Toes point where you move. Knees over toes.',
      },
      {
        time: 672,
        text: 'Weight split light on the balls of the feet. Quiet heels.',
      },
      { time: 690, text: 'Switch every four passes. Keep rhythm.' },
      { time: 708, text: 'Pulse. Three shuffle and set reps with no ball.' },
      { time: 726, text: 'Aim small. Sticker spot every pass.' },
      { time: 744, text: 'Count clean eight in a row. Smooth stops.' },
      { time: 762, text: 'Breathe out on pass. Head over hips.' },
      { time: 780, text: 'Last two reps each side. Soft touch.' },
      { time: 798, text: 'Transition. Reset space.' },

      // Stance 3: Setting — Stationary precision sets 960–1320
      {
        time: 960,
        text: 'Stance 3. Setting. Stationary Precision Sets. Hands high in the window. Hold for 3, 2, 1.',
      },
      { time: 978, text: 'Underhand toss to the forehead zone. Set to chest.' },
      { time: 996, text: 'Finger pads push. Elbows extend, then relax.' },
      { time: 1014, text: 'Lift from legs. Breathe out on the set.' },
      { time: 1032, text: 'Switch every six sets. Count together.' },
      { time: 1050, text: 'Feet quiet. Two tracks. Knees over toes.' },
      {
        time: 1068,
        text: 'Pulse. Three tiny knee bends, then one smooth set.',
      },
      { time: 1086, text: 'Aim small. Sticker spot on the shirt.' },
      { time: 1104, text: 'Count clean ten as a team. Calm rhythm.' },
      {
        time: 1122,
        text: 'Reset window. Thumbs and index make the diamond. Hold for 3, 2, 1.',
      },
      { time: 1140, text: 'Last round here. Soft hands, high hands.' },
      { time: 1158, text: 'Breathing steady. Tall spine.' },

      // Stance 4: Serving — Half step underhand serve 1320–1680
      {
        time: 1320,
        text: 'Stance 4. Serving. Half Step Serve. Step, swing, serve. Hold for 3, 2, 1.',
      },
      { time: 1338, text: 'Front toe points to target. Head over hips.' },
      { time: 1356, text: 'Ball stays still on front hand. No toss.' },
      { time: 1374, text: 'Flat open hand. Firm wrist. Bottom third contact.' },
      { time: 1392, text: 'Breathe out on contact. Path low and straight.' },
      { time: 1410, text: 'Switch every six serves. Count together.' },
      { time: 1428, text: 'Pulse. Two shadow step swings, then one serve.' },
      { time: 1446, text: 'Aim small. Hit the sticker spot or wall box.' },
      { time: 1464, text: 'Count clean ten as a team. Calm rhythm.' },
      { time: 1482, text: 'Reset stance. Two tracks. Knees soft.' },
      { time: 1500, text: 'Last two serves here. Smooth and accurate.' },
      { time: 1518, text: 'Transition. Shake out arms.' },

      // Stance 5: Combo — Receive, set, pass, serve 1680–2040
      {
        time: 1680,
        text: 'Stance 5. Combo. Receive, Set, Pass, Serve. Ready on two tracks. Hold for 3, 2, 1.',
      },
      {
        time: 1698,
        text: 'Toss to middle. Receive pass to chest. Partner catches.',
      },
      { time: 1716, text: 'Next toss to window. Quick set to chest.' },
      { time: 1734, text: 'Half step serve to chest or wall box.' },
      {
        time: 1752,
        text: 'Beat. Receive, set, serve, reset. One, two, three, four.',
      },
      { time: 1770, text: 'Foot angles. Toes and knees line up. Quiet heels.' },
      { time: 1788, text: 'Switch roles every four cycles. Count together.' },
      { time: 1806, text: 'Breathe on effort. Head over hips.' },
      { time: 1824, text: 'Aim small. Sticker spot each skill.' },
      { time: 1842, text: 'Pulse. Three tiny knee bends before the set.' },
      { time: 1860, text: 'Count clean four cycles in a row. Calm rhythm.' },
      { time: 1878, text: 'Last round here. Soft hands, smooth swing.' },

      // Accuracy Circuit 1 — Passing ladder 2040–2340
      {
        time: 2040,
        text: 'Accuracy circuit. Passing ladder. Middle, left shoulder, right shoulder, middle.',
      },
      { time: 2060, text: 'Switch roles. Repeat the pattern.' },
      { time: 2080, text: 'Lift from legs. Arms straight. Shoulders forward.' },
      { time: 2100, text: 'Aim small. Sticker spot each pass.' },
      { time: 2120, text: 'Count two perfect ladders in a row.' },
      { time: 2140, text: 'Breathe steady. Feet quiet, two tracks.' },

      // Accuracy Circuit 2 — Setting ladder 2340–2640
      {
        time: 2340,
        text: 'Accuracy circuit. Setting ladder. Middle, left, right, middle.',
      },
      { time: 2360, text: 'Switch roles. Repeat the ladder.' },
      { time: 2380, text: 'Finger pads push. Elbows out. High hands.' },
      { time: 2400, text: 'Aim small. Chest sticker every set.' },
      { time: 2420, text: 'Count two perfect ladders in a row.' },
      { time: 2440, text: 'Breathe out on contact. Tall spine.' },

      // Accuracy Circuit 3 — Serving ladder 2640–2940
      {
        time: 2640,
        text: 'Accuracy circuit. Serving ladder. Chest, left shoulder, right shoulder, chest.',
      },
      { time: 2660, text: 'Switch roles. Repeat the ladder.' },
      { time: 2680, text: 'Flat hand. Firm wrist. Bottom third contact.' },
      { time: 2700, text: 'Front toe to target. Head over hips.' },
      { time: 2720, text: 'Aim small. Hit the box or sticker spot.' },
      { time: 2740, text: 'Count two perfect ladders in a row.' },

      // Combo Flow Circuit — Pass, set, serve loop 2940–3300
      { time: 2940, text: 'Combo flow. Three in a row. Pass, set, serve. Go.' },
      { time: 2960, text: 'Switch roles. Repeat pass, set, serve.' },
      { time: 2980, text: 'Beat. Ready, pass, set, serve, reset.' },
      { time: 3000, text: 'Cues. Two tracks, knees over toes, tall spine.' },
      { time: 3020, text: 'Breathe on effort. Calm shoulders.' },
      { time: 3040, text: 'Aim small. Chest target each skill.' },
      { time: 3060, text: 'Count clean six flows as a team.' },
      { time: 3080, text: 'Last round. Smooth hands, quiet feet.' },

      // Cooldown 3300–3600
      {
        time: 3300,
        text: 'Cooldown. Shoulder stretch across the body. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 3330,
        text: 'Forearm and finger stretch. Palms down, then palms up. Hold for 3, 2, 1.',
      },
      { time: 3360, text: 'Wrist circles, ten each way. Shake out hands.' },
      {
        time: 3390,
        text: 'Neck stretch. Look left and right, slow. Hold for 3, 2, 1.',
      },
      {
        time: 3420,
        text: 'Slow belly breaths. In for four, out for four. Hold for 3, 2, 1. Team check. Quiet high fives. Great work. Space check and put gear away.',
      },
    ],
    timeTotal: 3424,
    equipment:
      'Foam volleyball (youth no-sting), partner; Optional: wall or taped targets; floor tape dots or small cones for spots; kneeling pad',
  },
];

export default volleyballPartnerDrills;
